// "Weiter fragen": the chat about one shot as a sheet from below (Dominik, 14.10.2026: "wenn man über das Ergebnis
// weiter chatten möchte"; board "Claude: Weiter fragen" 22.10.2026). It starts with the suggestion's heading and reason,
// answers to tap instead of an empty field, the field with a round send button and "Claude kann sich irren." below. The
// cloud keeps the conversation per account and shot, so it is the same on every device. The newest proposal (also the
// pre-infusion) can be adopted. A message that could not be sent goes back into the field.
import { useEffect, useId, useRef, useState } from 'react';
import type { ShotItem } from '@brewcore/app-contract';
import { applyView, chatChips, failureText, suggestionOf } from '../../ai/view.js';
import { shotBeanParts, shotMs, whenText } from '../../core/index.js';
import { FitLine, Icon, IconButton, Sheet } from '../components/index.js';
import { useMachine, useStore } from '../machine-context.js';
import { ApplyBox, useApply } from './ApplyBox.js';
import { useAssistant } from './use-assistant.js';

export function ChatSheet({ shot, onClose }: { shot: ShotItem; onClose: () => void }) {
  const { ctx, client, machineId } = useMachine();
  const { assistant } = useAssistant();
  const beans = useStore(client.beans(machineId));
  const c = assistant.shot(machineId, shot.id);
  const [draft, setDraft] = useState('');
  const [adopting, setAdopting] = useState(-1);
  const adopt = useApply();
  const titleId = useId();
  const fieldId = useId();
  const list = useRef<HTMLDivElement>(null);
  const unsent = c.unsent;
  useEffect(() => {
    if (!unsent) return;
    setDraft(unsent);
    assistant.clearUnsent(machineId, shot.id);
  }, [unsent, assistant, machineId, shot.id]);
  const messages = c.thread?.messages ?? [];
  const count = messages.length;
  const busy = c.busy === 'chat';
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, busy]);
  const currentBean = beans.items.find((b) => b.current) ?? null;
  // only the newest proposal can be adopted; older ones stay in the text
  const lastProposal = messages.findLastIndex((m) => m.role === 'assistant' && (m.changes?.length ?? 0) > 0);
  // the suggestion opens the chat as the card had it: its heading and reason (board "Claude: Weiter fragen")
  const first = suggestionOf(c);
  const chips = busy ? [] : chatChips(ctx.lang, shot, messages);
  const send = (text: string) => {
    const t = text.trim();
    if (!t || c.busy) return;
    setDraft('');
    void assistant.chat(machineId, shot.id, t, ctx.lang);
  };
  const submit = () => send(draft);
  const bean = shotBeanParts(ctx, shot, beans);
  return (
    <Sheet
      title={
        <span className="ai-chat-title">
          <Icon name="sparkle" size="sm" className="ai-box-mark" />
          {ctx.tr('aiClaude')}
        </span>
      }
      titleId={titleId}
      onClose={onClose}
      closeLabel={ctx.tr('aiChatClose')}
      closeTestId="claude-chat-close"
      testId="claude-chat"
    >
      {/* one line: the roaster drops out first, then the day (WRITING.md 0a) */}
      <FitLine
        className="hint ai-chat-shot"
        parts={[ctx.tr('aiChatShot', { when: whenText(shotMs(shot), ctx.lang, ctx.now) }), ...bean]}
      />
      <div className="ai-msgs" ref={list} aria-live="polite" data-testid="claude-chat-msgs">
        {count === 0 && !busy && <p className="hint">{ctx.tr('aiChatEmpty')}</p>}
        {messages.map((m, i) => {
          if (first?.index === i && first.message.head)
            return (
              <div key={i} className="ai-chat-first" data-testid={`claude-msg-${i}`}>
                <p className="ai-box-head">{first.message.head}</p>
                <p className="ai-box-why">{m.text}</p>
              </div>
            );
          const sending = adopt.sending && adopting === i ? adopt.sending : null;
          const apply =
            sending ?? (i === lastProposal && !busy ? applyView(ctx, shot, m, currentBean) : null);
          return (
            <div
              key={i}
              className={`ai-msg ai-msg-${m.role === 'user' ? 'u' : 'a'}`}
              data-testid={`claude-msg-${i}`}
            >
              <span className="visually-hidden">{m.role === 'user' ? ctx.tr('aiYou') : 'Claude'}: </span>
              <p>{m.text}</p>
              {apply && (
                <ApplyBox
                  view={apply}
                  sending={sending !== null}
                  testId="claude-chat-apply"
                  onApply={() => {
                    setAdopting(i);
                    void adopt.apply(apply, () => void assistant.markApplied(machineId, shot.id, i));
                  }}
                />
              )}
            </div>
          );
        })}
        {busy && c.pending && (
          <div className="ai-msg ai-msg-u" data-testid="claude-msg-pending">
            <p>{c.pending}</p>
          </div>
        )}
        {busy && (
          <div className="ai-msg ai-msg-a" aria-busy="true" data-testid="claude-msg-live">
            <p className="ai-text is-live">{ctx.tr('aiThinking')}</p>
          </div>
        )}
      </div>
      {c.error && c.errorFor === 'chat' && !busy && (
        <p className="field-error" role="alert" data-testid="claude-chat-error">
          {failureText(ctx.lang, c.error)}
        </p>
      )}
      {chips.length > 0 && (
        <div className="choice ai-chips" data-testid="claude-chips">
          {chips.map((chip) => (
            <button key={chip} type="button" className="choice-item" onClick={() => send(chip)}>
              {chip}
            </button>
          ))}
        </div>
      )}
      <div className="ai-chat-foot">
        <label className="visually-hidden" htmlFor={fieldId}>
          {ctx.tr('aiChatPh')}
        </label>
        <textarea
          id={fieldId}
          className="field-input ai-chat-input"
          rows={1}
          maxLength={1000}
          value={draft}
          placeholder={ctx.tr('aiChatPh')}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          data-testid="claude-chat-input"
        />
        <IconButton
          icon="arrowRight"
          label={ctx.tr('aiChatSend')}
          className="ai-send"
          aria-busy={busy || undefined}
          disabled={!!c.busy || !draft.trim()}
          onClick={submit}
          data-testid="claude-chat-send"
        />
      </div>
      <p className="ai-chat-note">{ctx.tr('aiMistakes')}</p>
    </Sheet>
  );
}
